import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { SoftwareLicenca, LicencaFormData } from '@/types/software';
import { toast } from '@/hooks/use-toast';
import { useClientFilter } from '@/hooks/useClientFilter';

export const useSoftwareLicencas = (softwareId?: string) => {
  const { getClientFilter } = useClientFilter();
  const clientId = getClientFilter();

  return useQuery({
    queryKey: ['software-licencas', softwareId, clientId],
    queryFn: async () => {
      if (!softwareId) return [];
      
      const { data, error } = await supabase
        .from('cmdb_software_licencas')
        .select(`
          *,
          usuario:users(id, name, email),
          ativo:cmdb_ativos(id, nome)
        `)
        .eq('software_id', softwareId)
        .order('criado_em', { ascending: false });

      if (error) throw error;
      return data as SoftwareLicenca[];
    },
    enabled: !!softwareId,
  });
};

export const useCreateLicenca = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: LicencaFormData) => {
      const { data: result, error } = await supabase
        .from('cmdb_software_licencas')
        .insert(data)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['software-licencas', variables.software_id] });
      queryClient.invalidateQueries({ queryKey: ['softwares'] });
      toast({
        title: 'Licença criada',
        description: 'A licença foi criada com sucesso.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao criar licença',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

export const useCreateMultipleLicencas = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ softwareId, quantidade }: { softwareId: string; quantidade: number }) => {
      const licencas = Array.from({ length: quantidade }, () => ({
        software_id: softwareId,
        status: 'disponivel' as const,
      }));

      const { data: result, error } = await supabase
        .from('cmdb_software_licencas')
        .insert(licencas)
        .select();

      if (error) throw error;
      return result;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['software-licencas', variables.softwareId] });
      queryClient.invalidateQueries({ queryKey: ['softwares'] });
      toast({
        title: 'Licenças criadas',
        description: `${variables.quantidade} licenças foram criadas com sucesso.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao criar licenças',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

export const useAtribuirLicenca = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      licencaId, 
      usuarioId, 
      ativoId,
      observacoes 
    }: { 
      licencaId: string; 
      usuarioId: string; 
      ativoId?: string;
      observacoes?: string;
    }) => {
      const { data: result, error } = await supabase
        .from('cmdb_software_licencas')
        .update({
          usuario_id: usuarioId,
          ativo_id: ativoId || null,
          status: 'em_uso',
          data_atribuicao: new Date().toISOString(),
          observacoes,
        })
        .eq('id', licencaId)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['software-licencas'] });
      queryClient.invalidateQueries({ queryKey: ['softwares'] });
      toast({
        title: 'Licença atribuída',
        description: 'A licença foi atribuída ao usuário com sucesso.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao atribuir licença',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

export const useLiberarLicenca = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (licencaId: string) => {
      const { data: result, error } = await supabase
        .from('cmdb_software_licencas')
        .update({
          usuario_id: null,
          ativo_id: null,
          status: 'disponivel',
          data_atribuicao: null,
          observacoes: null,
        })
        .eq('id', licencaId)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['software-licencas'] });
      queryClient.invalidateQueries({ queryKey: ['softwares'] });
      toast({
        title: 'Licença liberada',
        description: 'A licença foi liberada com sucesso.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao liberar licença',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteLicenca = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (licencaId: string) => {
      const { error } = await supabase
        .from('cmdb_software_licencas')
        .delete()
        .eq('id', licencaId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['software-licencas'] });
      queryClient.invalidateQueries({ queryKey: ['softwares'] });
      toast({
        title: 'Licença excluída',
        description: 'A licença foi excluída com sucesso.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao excluir licença',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

export const useMinhasLicencas = () => {
  const { getClientFilter } = useClientFilter();
  const clientId = getClientFilter();

  return useQuery({
    queryKey: ['minhas-licencas', clientId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('cmdb_software_licencas')
        .select(`
          *,
          software:cmdb_softwares(id, nome, versao, fabricante:fabricantes(nome))
        `)
        .eq('usuario_id', user.id)
        .eq('status', 'em_uso');

      if (error) throw error;
      return data as SoftwareLicenca[];
    },
  });
};

export const useTodasLicencasEmUso = () => {
  const { getClientFilter, clientLoading, isAdmin } = useClientFilter();
  const clientId = getClientFilter();

  return useQuery({
    queryKey: ['todas-licencas-em-uso', clientId],
    queryFn: async () => {
      let query = supabase
        .from('cmdb_software_licencas')
        .select(`
          *,
          usuario:users(id, name, email),
          ativo:cmdb_ativos(id, nome),
          software:cmdb_softwares!inner(id, nome, versao, client_id, fabricante:fabricantes(nome))
        `)
        .eq('status', 'em_uso')
        .order('data_atribuicao', { ascending: false });

      // Filtrar pelo client_id do software associado
      if (!isAdmin && clientId) {
        query = query.eq('software.client_id', clientId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as (SoftwareLicenca & { software: { id: string; nome: string; versao?: string; client_id?: string; fabricante?: { nome: string } } })[];
    },
    enabled: !clientLoading,
  });
};
